/**
 * Fiches d'information pédagogiques pour le planétarium.
 *
 * Sources des chiffres :
 *   - NASA Planetary Fact Sheet : https://nssdc.gsfc.nasa.gov/planetary/factsheet/
 *   - JPL Horizons (positions live)
 *   - JPL Small Body Database (objets transneptuniens)
 *
 * Aucun chiffre inventé. Les valeurs en plage sont marquées "≈" ou min/max.
 * Pour Planet 9 : 100 % hypothétique, calibré sur Brown & Batygin 2021.
 */

export type BodyType =
  | 'rocky'         // tellurique
  | 'gas_giant'     // géante gazeuse
  | 'ice_giant'     // géante glacée
  | 'dwarf_planet'  // planète naine
  | 'star'
  | 'hypothesis';   // Planet 9

export interface PlanetInfo {
  /** Clé Horizons (ID numérique pour les majeurs) ou key catalogue. */
  id: string;
  name_fr: string;
  name_en: string;
  type: BodyType;
  symbol: string;            // emoji ou caractère astronomique
  /** Diamètre équatorial en km. */
  diameter_km: number;
  /** Ratio de diamètre par rapport à la Terre. */
  diameter_vs_earth: number;
  /** Distance moyenne au Soleil en UA. */
  distance_au: number;
  /** Période orbitale (année locale) en jours terrestres. */
  year_earth_days: number;
  /** Période de rotation sidérale en heures terrestres. Si négatif → rotation rétrograde. */
  day_length_hours: number;
  /** Température moyenne ou plage [min, max] en °C à la surface ou au niveau 1 bar. */
  temperature_c: number | [number, number];
  /** Gravité de surface relative à la Terre (Terre = 1.0). */
  gravity_vs_earth: number;
  /** Nombre de lunes connues (NASA, mise à jour 2024). */
  moons_count: number;
  /** Particularité principale en une phrase. */
  highlight: string;
  /** 3 fun facts maximum, courts, vérifiés. */
  fun_facts: string[];
  /** Résumé en 2 lignes max. */
  summary: string;
  /** Note d'incertitude pour Planet 9 ; null sinon. */
  uncertainty?: string;
}

/** Pour chaque planète : poids d'une personne de 70 kg. */
export function weightOnBody(weightKg: number, body: PlanetInfo): number {
  return Math.round(weightKg * body.gravity_vs_earth * 10) / 10;
}

/** Convertit jours terrestres en chaîne lisible (ex: "165 ans"). */
export function formatYear(days: number): string {
  if (days < 1.5) return `${(days * 24).toFixed(1)} h`;
  if (days < 365) return `${Math.round(days)} jours`;
  const years = days / 365.25;
  if (years < 10) return `${years.toFixed(1)} ans`;
  return `${Math.round(years).toLocaleString('fr-FR')} ans`;
}

/** Convertit heures terrestres en chaîne lisible. */
export function formatDay(hours: number): string {
  const abs = Math.abs(hours);
  const note = hours < 0 ? ' (rétrograde)' : '';
  if (abs < 24) return `${abs.toFixed(1)} h${note}`;
  return `${(abs / 24).toFixed(1)} jours${note}`;
}

/** Convertit température(s) en chaîne lisible. */
export function formatTemperature(t: number | [number, number]): string {
  if (Array.isArray(t)) return `${t[0]}°C → ${t[1]}°C`;
  return `${t}°C`;
}

// ─── Données NASA / IAU vérifiées ──────────────────────────────────────────────

export const PLANET_INFO: Record<string, PlanetInfo> = {
  // Soleil — pour info, même s'il a son propre composant
  '10': {
    id: '10',
    name_fr: 'Soleil',
    name_en: 'Sun',
    type: 'star',
    symbol: '☉',
    diameter_km: 1_392_700,
    diameter_vs_earth: 109.2,
    distance_au: 0,
    year_earth_days: 0,
    day_length_hours: 25.05 * 24, // rotation différentielle ~25 j à l'équateur
    temperature_c: 5500,
    gravity_vs_earth: 27.95,
    moons_count: 0,
    highlight: 'Étoile naine jaune (G2V), centre de gravité du système solaire.',
    fun_facts: [
      'Le Soleil contient 99,86 % de la masse totale du système solaire.',
      'Il faut 8 minutes 20 secondes à la lumière pour atteindre la Terre.',
      'Sa surface fait 5500°C, mais sa couronne dépasse 1 million de degrés.',
    ],
    summary: 'Étoile centrale du système solaire. Ses 5,5 milliards d\'années en font une étoile encore jeune.',
  },

  '199': {
    id: '199',
    name_fr: 'Mercure',
    name_en: 'Mercury',
    type: 'rocky',
    symbol: '☿',
    diameter_km: 4879,
    diameter_vs_earth: 0.38,
    distance_au: 0.387,
    year_earth_days: 88.0,
    day_length_hours: 1407.6, // 58.6 jours terrestres
    temperature_c: [-173, 427],
    gravity_vs_earth: 0.38,
    moons_count: 0,
    highlight: 'La plus petite planète, et la plus proche du Soleil.',
    fun_facts: [
      'Une année dure 88 jours : tu fêterais ton anniversaire 4 fois par an.',
      'Une journée mercurienne (lever-coucher) dure 176 jours terrestres — plus long qu\'une année.',
      'Écart de 600°C entre le jour et la nuit : aucun autre corps du système solaire ne fait pire.',
    ],
    summary: 'Petit monde rocheux brûlé d\'un côté, gelé de l\'autre. Un orbite ultra-rapide autour du Soleil.',
  },

  '299': {
    id: '299',
    name_fr: 'Vénus',
    name_en: 'Venus',
    type: 'rocky',
    symbol: '♀',
    diameter_km: 12_104,
    diameter_vs_earth: 0.95,
    distance_au: 0.723,
    year_earth_days: 224.7,
    day_length_hours: -5832.5, // 243 j rétrograde
    temperature_c: 464,
    gravity_vs_earth: 0.91,
    moons_count: 0,
    highlight: 'La planète la plus chaude — son atmosphère de CO₂ piège la chaleur.',
    fun_facts: [
      'Une journée vénusienne (243 j) dure plus longtemps qu\'une année (225 j).',
      'Vénus tourne à l\'envers : sur Vénus, le Soleil se lève à l\'ouest.',
      'Sa pression atmosphérique au sol est 92 fois celle de la Terre — comme à 900 m sous l\'océan.',
    ],
    summary: 'Jumelle infernale de la Terre. Effet de serre extrême, surface à 464°C, pluies d\'acide sulfurique en altitude.',
  },

  '399': {
    id: '399',
    name_fr: 'Terre',
    name_en: 'Earth',
    type: 'rocky',
    symbol: '🜨',
    diameter_km: 12_756,
    diameter_vs_earth: 1.0,
    distance_au: 1.0,
    year_earth_days: 365.25,
    day_length_hours: 23.93,
    temperature_c: 15,
    gravity_vs_earth: 1.0,
    moons_count: 1,
    highlight: 'Le seul monde connu hébergeant la vie — eau liquide, atmosphère d\'oxygène.',
    fun_facts: [
      'On voyage à 107 000 km/h sans s\'en rendre compte (vitesse orbitale autour du Soleil).',
      'L\'axe de la Terre est incliné de 23,5° — c\'est ce qui crée les saisons.',
      'Le noyau interne tourne légèrement plus vite que le manteau.',
    ],
    summary: 'Notre planète. La référence pour comparer toutes les autres. 71 % d\'eau, 1 lune.',
  },

  '499': {
    id: '499',
    name_fr: 'Mars',
    name_en: 'Mars',
    type: 'rocky',
    symbol: '♂',
    diameter_km: 6792,
    diameter_vs_earth: 0.53,
    distance_au: 1.524,
    year_earth_days: 687.0,
    day_length_hours: 24.62,
    temperature_c: -63,
    gravity_vs_earth: 0.38,
    moons_count: 2,
    highlight: 'La planète rouge — oxyde de fer en surface, traces d\'eau ancienne.',
    fun_facts: [
      'Mars abrite Olympus Mons, le plus grand volcan du système solaire (22 km de haut).',
      'Une journée martienne dure presque comme la nôtre (24 h 37 min) — d\'où le nom "sol".',
      'Phobos, l\'une des deux lunes, se rapproche de 1,8 cm par an : elle s\'écrasera dans ~50 millions d\'années.',
    ],
    summary: 'Désert froid couvert d\'oxyde de fer. Cible numéro 1 de l\'exploration spatiale future.',
  },

  '599': {
    id: '599',
    name_fr: 'Jupiter',
    name_en: 'Jupiter',
    type: 'gas_giant',
    symbol: '♃',
    diameter_km: 142_984,
    diameter_vs_earth: 11.2,
    distance_au: 5.203,
    year_earth_days: 4332.6,
    day_length_hours: 9.93,
    temperature_c: -108,
    gravity_vs_earth: 2.53,
    moons_count: 95,
    highlight: 'La géante gazeuse la plus massive — 2,5× toutes les autres planètes réunies.',
    fun_facts: [
      'Sur Jupiter, un objet de 70 kg pèserait 177 kg : tu serais écrasé en quelques minutes.',
      'La Grande Tache Rouge est une tempête plus large que la Terre, observée depuis au moins 350 ans.',
      'Jupiter a un anneau ténu, découvert seulement en 1979 par Voyager 1.',
    ],
    summary: 'Géante gazeuse aux 95 lunes. Tournée si vite (10 h/jour) qu\'elle est aplatie aux pôles.',
  },

  '699': {
    id: '699',
    name_fr: 'Saturne',
    name_en: 'Saturn',
    type: 'gas_giant',
    symbol: '♄',
    diameter_km: 120_536,
    diameter_vs_earth: 9.45,
    distance_au: 9.537,
    year_earth_days: 10_759,
    day_length_hours: 10.66,
    temperature_c: -139,
    gravity_vs_earth: 1.07,
    moons_count: 146,
    highlight: 'Anneaux les plus spectaculaires du système solaire — composés à 99 % de glace.',
    fun_facts: [
      'Saturne est moins dense que l\'eau : si on trouvait un océan assez grand, elle flotterait.',
      'Ses anneaux ne mesurent que 10 m d\'épaisseur, mais 282 000 km de large.',
      'Une de ses lunes, Titan, est la seule du système solaire avec une atmosphère dense (azote, méthane).',
    ],
    summary: 'Géante gazeuse à 146 lunes connues. Ses anneaux de glace sont visibles avec un télescope amateur.',
  },

  '799': {
    id: '799',
    name_fr: 'Uranus',
    name_en: 'Uranus',
    type: 'ice_giant',
    symbol: '♅',
    diameter_km: 51_118,
    diameter_vs_earth: 4.01,
    distance_au: 19.19,
    year_earth_days: 30_687,
    day_length_hours: -17.24, // rotation rétrograde
    temperature_c: -197,
    gravity_vs_earth: 0.89,
    moons_count: 28,
    highlight: 'Géante glacée couchée sur le côté — son axe est incliné de 98°.',
    fun_facts: [
      'Uranus roule sur son orbite : ses pôles passent 42 ans dans le jour, puis 42 ans dans la nuit.',
      'Sa couleur bleu-vert vient du méthane atmosphérique qui absorbe le rouge.',
      'Une année uranienne (84 ans) signifie qu\'aucun humain n\'a vu Uranus revenir au même point depuis sa découverte en 1781.',
    ],
    summary: 'Géante glacée méconnue, basculée sur le côté. Visitée une seule fois par Voyager 2 en 1986.',
  },

  '899': {
    id: '899',
    name_fr: 'Neptune',
    name_en: 'Neptune',
    type: 'ice_giant',
    symbol: '♆',
    diameter_km: 49_528,
    diameter_vs_earth: 3.88,
    distance_au: 30.07,
    year_earth_days: 60_190,
    day_length_hours: 16.11,
    temperature_c: -201,
    gravity_vs_earth: 1.14,
    moons_count: 16,
    highlight: 'La géante glacée la plus lointaine — vents les plus rapides du système solaire (2100 km/h).',
    fun_facts: [
      'Une année neptunienne dure 165 ans : depuis sa découverte en 1846, elle vient seulement de boucler son premier tour.',
      'Ses vents soufflent à plus de 2100 km/h, plus rapide que la vitesse du son sur Terre.',
      'Découverte par calcul mathématique avant d\'être observée — Le Verrier a prédit sa position grâce aux perturbations d\'Uranus.',
    ],
    summary: 'Géante glacée à -200°C, balayée par des vents supersoniques. Triton, sa plus grande lune, gèle de l\'azote.',
  },

  // ─── Petits corps notables (clé = catalogue solar_system_targets.json) ────

  pluto: {
    id: '999',
    name_fr: 'Pluton',
    name_en: 'Pluto',
    type: 'dwarf_planet',
    symbol: '♇',
    diameter_km: 2376,
    diameter_vs_earth: 0.19,
    distance_au: 39.48,
    year_earth_days: 90_560, // 248 ans
    day_length_hours: -153.3, // ~6.4 j rétrograde
    temperature_c: -229,
    gravity_vs_earth: 0.063,
    moons_count: 5,
    highlight: 'Reclassée planète naine en 2006 — découverte en 1930, visitée par New Horizons en 2015.',
    fun_facts: [
      'Pluton est plus petit que la Lune : son diamètre fait 2/3 de celui de notre satellite.',
      'Sa lune Charon est si grande que les deux corps tournent autour d\'un point situé hors de Pluton.',
      'Sa surface présente une plaine en forme de cœur (Sputnik Planitia), faite d\'azote gelé.',
    ],
    summary: 'Plus grand objet de la ceinture de Kuiper. Atmosphère ténue d\'azote qui gèle quand Pluton s\'éloigne du Soleil.',
  },

  sedna: {
    id: '90377',
    name_fr: 'Sedna',
    name_en: 'Sedna',
    type: 'dwarf_planet',
    symbol: '⯲',
    diameter_km: 995,
    diameter_vs_earth: 0.078,
    distance_au: 506,
    year_earth_days: 4_200_000, // ≈ 11 400 ans
    day_length_hours: 10.27,
    temperature_c: -261,
    gravity_vs_earth: 0.026,
    moons_count: 0,
    highlight: 'Sednoid découvert en 2003 — sa lointaine orbite est l\'un des indices indirects de Planet 9.',
    fun_facts: [
      'Une orbite complète dure ~11 400 ans : Sedna n\'est repassée à son périhélie qu\'une fois depuis la fin de la dernière glaciation.',
      'Sa surface est l\'une des plus rouges du système solaire — comparable à Mars en intensité.',
      'Son périhélie à 76 UA reste très loin de Neptune : impossible que Neptune soit la cause de cette orbite.',
    ],
    summary: 'Sednoid mystérieux à plus de 500 UA. Sa trajectoire suggère un objet massif lointain — peut-être Planet 9.',
    uncertainty: 'Diamètre estimé entre 900 et 1100 km — Sedna est trop loin pour être directement résolue.',
  },

  // ─── Planet 9 — entièrement hypothétique ───────────────────────────────────

  planet9: {
    id: 'planet9',
    name_fr: 'Planet 9 (hypothétique)',
    name_en: 'Planet Nine (hypothetical)',
    type: 'hypothesis',
    symbol: '?',
    diameter_km: 25_000, // ~2× Terre, estimation Brown & Batygin
    diameter_vs_earth: 2.0,
    distance_au: 500, // moyenne sur les priors
    year_earth_days: 4_400_000, // ~12 000 ans
    day_length_hours: 10, // pure spéculation, ordre de grandeur d'une géante
    temperature_c: -226,
    gravity_vs_earth: 1.5,
    moons_count: 0,
    highlight: 'NON DÉTECTÉE. Hypothèse formulée pour expliquer le clustering des orbites ETNO.',
    fun_facts: [
      'Aucun télescope n\'a jamais observé Planet 9 : son existence est déduite des orbites de 6 ETNOs alignées.',
      'Si elle existe, elle ferait ~6 masses terrestres et orbiterait à 380–800 UA — 10× plus loin que Neptune.',
      'Plusieurs surveys ciblés (PS1, DES, ZTF, Subaru) n\'ont rien trouvé dans ses zones d\'exclusion.',
    ],
    summary: 'Hypothèse théorique pour expliquer les anomalies orbitales des objets transneptuniens extrêmes.',
    uncertainty: 'AUCUN paramètre n\'est mesuré : tous sont des estimations à partir des contraintes dynamiques (Brown & Batygin 2021, wide-orbit 2024–2025). Voir /planet-nine pour la carte probabiliste.',
  },
};

// ─── Helpers d'accès ──────────────────────────────────────────────────────────

export function getInfoById(id: string): PlanetInfo | null {
  return PLANET_INFO[id] ?? null;
}

export function getInfoBySmallBodyKey(key: string): PlanetInfo | null {
  return PLANET_INFO[key] ?? null;
}

export const TYPE_LABELS: Record<BodyType, { label: string; color: string; help: string }> = {
  rocky:        { label: 'Rocheuse',      color: '#d97a55', help: 'Planète tellurique : surface solide composée de roches et métaux.' },
  gas_giant:    { label: 'Géante gazeuse', color: '#e3c590', help: 'Atmosphère épaisse d\'hydrogène et d\'hélium, pas de surface solide.' },
  ice_giant:    { label: 'Géante glacée',  color: '#5b8ad4', help: 'Géante composée d\'eau, méthane et ammoniac sous forme glacée et fluide.' },
  dwarf_planet: { label: 'Planète naine',  color: '#9fdcdc', help: 'Corps en équilibre hydrostatique mais qui n\'a pas dégagé son orbite (def. IAU 2006).' },
  star:         { label: 'Étoile',         color: '#ffe0a3', help: 'Corps qui produit de l\'énergie par fusion nucléaire en son cœur.' },
  hypothesis:   { label: 'HYPOTHÉTIQUE',   color: '#c08cff', help: 'Existence non confirmée — déduite indirectement, jamais observée.' },
};

export const FIELD_HELP: Record<string, string> = {
  diameter:        'Diamètre équatorial mesuré au niveau du nuage de référence (1 bar pour les géantes).',
  distance:        'Distance moyenne au Soleil (demi-grand axe orbital).',
  year:            'Période de révolution autour du Soleil — durée d\'une "année" locale.',
  day:             'Période de rotation sidérale (un tour sur soi-même par rapport aux étoiles fixes).',
  gravity:         'Gravité de surface relative à la Terre. Sur Terre, g = 9,81 m/s².',
  temperature:     'Température moyenne en surface ou à la pression de référence (1 bar).',
  moons:           'Nombre de satellites naturels confirmés (NASA, mise à jour 2024).',
};
